import React, { useState, useEffect, useCallback } from "react";
import { Card, CardHeader, CardBody, Container, Row, Col } from "reactstrap";
import Header from "components/Headers/Header";
import Dataservices from "../../services/requestApi";
import AllProductPackage from "components/ProductPackage/AllProductPackage";
import AddProductPackage from "components/ProductPackage/AddProductPackage";
import { message } from "antd";
import Loader from "components/loader/Loader";

const ProductPackage = ({ location }) => {
  // 🧩 Extract product id & name from route location
  const { _id, name } = location.state;

  // 🔧 Define states
  const [quantities, setQuantities] = useState([]); // size-wise quantities
  const [Allvariant, setAllVariant] = useState([]); // list of all variants
  const [colour, setColour] = useState(""); // selected color
  const [image, setImage] = useState(null); // image files
  const [thumbnail, setThumbnail] = useState(null); // image previews
  const [size, setSize] = useState([]); // sizes array (e.g., ["S","M","L"])
  const [price, setPrice] = useState(""); // price
  const [mrp, setMrp] = useState(""); // MRP
  const [update, setUpdate] = useState(false); // update mode toggle
  const [variantid, setVariantId] = useState(""); // variant ID for update
  const [loading, setLoading] = useState(false); // loader state

  // 📦 Fetch all variants for a given product
  const getAllVariant = useCallback(async () => {
    try {
      setLoading(true);
      const res = await Dataservices.ProductAllVariant(_id);
      if (res.data.data) {
        setAllVariant(res.data.data);
        setLoading(false);
      }
    } catch (e) {
      console.log(e);
      setLoading(false);
    }
  }, [_id]);

  // 🕐 Run once when component mounts
  useEffect(() => {
    getAllVariant();
  }, [getAllVariant]);

  // ➕ Add new product variant
  const AddVariant = async (e) => {
    e.preventDefault();
    const data = new FormData();

    // 🧱 Prepare form data
    data.append("product_id", _id);
    data.append("colour", colour);
    image.forEach((element) => data.append("image", element));
    data.append("size", size);
    data.append("price", price);
    data.append("mrp", mrp);
    data.append("quantities", JSON.stringify(quantities)); // store quantities as JSON

    try {
      const res = await Dataservices.ProductCreateVariant(data);
      console.log(res.data);

      if (res.data.status_code === 200) {
        // ✅ Success message
        message.success(res.data.message);
        getAllVariant();

        // 🔄 Reset fields
        setColour("");
        setPrice("");
        setMrp("");
        setImage(null);
        setThumbnail(null);
        setSize([]);
        setQuantities({});
      } else {
        message.error(res.data.message);
      }
    } catch (e) {
      console.log(e);
    }
  };

  // ✏️ When clicking "Edit" button, pre-fill fields for update
  const variantUpdateStatus = async (data) => {
    setUpdate(true);
    setColour(data.colour);
    setImage(data.image);
    setThumbnail(data.image);

    // Convert size string to array
    if (data.size) {
      const result = data.size.split(",");
      setSize(result);
    }

    // Set quantities (convert if needed)
    if (data.quantities) {
      setQuantities(Object.fromEntries(Object.entries(data.quantities)));
    }

    setMrp(data.mrp);
    setPrice(data.price);
    setVariantId(data._id);
  };

  // 🔄 Toggle variant status (Active/Inactive)
  const status = async (e, val) => {
    e.preventDefault();
    try {
      const data = new FormData();
      data.append("status", !val.status);
      await Dataservices.ProductUpdateVariant(val._id, data);
      window.location.reload(false);
    } catch (error) {
      console.log(error);
    }
  };

  // 🔧 Update existing product variant
  const variantUpdate = async (e) => {
    e.preventDefault();
    const data = new FormData();

    // 🧱 Prepare data for update
    data.append("product_id", _id);
    data.append("colour", colour);

    // Add existing or new images
    if (image && image.length > 0) {
      image.forEach((element) => {
        data.append("image", element?.image_link ?? element);
      });
    }

    // Sizes as comma-separated
    data.append("size", size);
    data.append("price", price);
    data.append("mrp", mrp);
    data.append("quantities", JSON.stringify(quantities));

    try {
      const res = await Dataservices.ProductUpdateVariant(variantid, data);

      if (res.data.status_code === 200) {
        message.success("Variant updated successfully!");
        setUpdate(false);
        getAllVariant();

        // Reset fields
        setColour("");
        setPrice("");
        setMrp("");
        setImage(null);
        setThumbnail(null);
        setSize([]);
        setQuantities({});
      } else {
        message.error(res.data.message || "Something went wrong!");
      }
    } catch (error) {
      console.log(error);
    }
  };

  // ❌ Delete variant
  const deletePackage = async (e, id) => {
    e.preventDefault();
    try {
      await Dataservices.ProductDeleteVariant(id);
      getAllVariant();
    } catch (error) {
      console.log(error);
    }
  };

  // 🧠 JSX Return
  return (
    <>
      <Header show={false} />

      <Container className="mt--7" fluid>
        <Row className="justify-content-center">
          {/* 🧩 Left Side: Add or Update Variant */}
          <Col xl="4">
            <Card className="card-profile shadow mb-5">
              <CardHeader className="border-0">
                <Row className="align-items-center">
                  <Col xs="12">
                    <h3 className="mb-0">Add Product Variant</h3>
                  </Col>
                </Row>
              </CardHeader>

              <CardBody className="pt-0 pt-md-4">
                <Row>
                  <Col lg="12">
                    <AddProductPackage
                      colour={colour}
                      setcolour={setColour}
                      price={price}
                      setprice={setPrice}
                      mrp={mrp}
                      setmrp={setMrp}
                      setimage={setImage}
                      thumbnail={thumbnail}
                      setThumbnail={setThumbnail}
                      size={size}
                      setSize={setSize}
                      quantities={quantities}
                      setQuantities={setQuantities}
                      addvariant={AddVariant}
                      updatevariant={variantUpdate}
                      statusupdate={update}
                    />
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>

          {/* 📋 Right Side: All Variants List */}
          <Col xl="8">
            <Card className="shadow">
              <CardHeader className="bg-white border-0">
                <Row className="align-items-center">
                  <Col xs="12">
                    <h3 className="mb-0">All Variants in {name}</h3>
                  </Col>
                </Row>
              </CardHeader>

              <CardBody>
                <Loader show={loading} />
                <AllProductPackage
                  allvariant={Allvariant}
                  updatevariant={variantUpdateStatus}
                  deletevariant={deletePackage}
                  status={status}
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default ProductPackage;
